import { useAuth } from '@opendoor/auth-fe/context';
import { useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowserAllowPartialData } from 'components/api/zeusBrowserClient';
import { captureError } from 'helpers/captureError';

import type {
  HubBuyerListingFragment,
  HubBuyerListingsAndToursQuery,
} from './__generated__/hub-buyer-data.zeus';
import { HubBuyerListingsAndToursDocument } from './__generated__/hub-buyer-data.zeus';

export type HubBuyingFavorite = HubBuyerListingFragment;
export type HubBuyingTour =
  HubBuyerListingsAndToursQuery['customerAccount']['buyer']['tours'][number];

const isPurchasableListing = (
  listing: HubBuyerListingFragment | null,
): listing is HubBuyerListingFragment =>
  listing?.listingState === 'ON_THE_MARKET' || listing?.listingState === 'IN_CONTRACT';

export function useHubBuyerListingsAndTours() {
  const { authentication } = useAuth();
  const customerUuid =
    authentication.state === 'authenticated' ? authentication.user.customerUuid : null;
  return useQuery({
    queryKey: ['hub', 'buyer-listings-and-tours', customerUuid],
    enabled: customerUuid !== null,
    meta: { captureErrorContext: 'HubBuyerListingsAndTours' },
    queryFn: async ({ signal }) => {
      const result = await requestZeusFromBrowserAllowPartialData({
        document: HubBuyerListingsAndToursDocument,
        variables: {},
        signal,
      });
      for (const error of result.errors) {
        captureError(error, 'HubBuyerListingsAndTours');
      }
      const buyer = result.data?.customerAccount.buyer;
      return {
        tours:
          buyer?.tours.map((tour) => ({
            ...tour,
            listing: isPurchasableListing(tour.listing) ? tour.listing : null,
          })) ?? [],
        favorites:
          buyer?.favorites.flatMap(({ listing }) =>
            isPurchasableListing(listing) && listing.fullAddress ? [listing] : [],
          ) ?? [],
      };
    },
  });
}
